/**
 * examentrainingScoring — regelgebaseerd nakijken voor de examentrainingsmodule.
 *
 * Pure, client-side module zonder imports, in de geest van src/utils/scoringUtils.js.
 * Dit is de nakijkkern die het hoofdplatform (testwisely) kan hergebruiken, met
 * name het numerieke antwoordtype met tolerantie dat daar ontbreekt.
 *
 * Herzien 2026-08-26 na adversariële review. Opgeloste fouten:
 *  - "350 m3" werd 3503 (het cijfer in de eenheid overleefde het globale strippen);
 *  - "1/2" werd 12 (de schuine streep werd weggestript);
 *  - "50%" werd stil door 100 gedeeld en dus 0,5;
 *  - "twaalf" werd 0 (Number('') === 0);
 *  - unicode-minteken (−) verloor het teken.
 */

/**
 * Zet een deelnemer-invoer om naar een getal. Robuust tegen de klassieke valkuilen.
 *
 * Aanpak: we pakken het EERSTE getal-token uit de invoer in plaats van globaal alle
 * niet-cijfers te strippen. Zo blijft een meegetypte eenheid ("350 m³", "7 cm")
 * buiten het getal, ook als die eenheid zelf een cijfer bevat ("m3", "cm2").
 *
 * @returns {number|null} het getal, of null als er geen getal in zit.
 */
export function parseGetal(invoer) {
  if (invoer === null || invoer === undefined) return null;
  let s = String(invoer).trim();
  if (s === '') return null;

  s = s.replace(/−/g, '-'); // unicode-minteken → gewone min

  // Eerste getal-token: optioneel teken, cijfers met , en . als scheiders,
  // eventueel gevolgd door /noemer voor een eenvoudige breuk. Stopt bij de
  // eerste letter of spatie ná het getal — dus de eenheid blijft erbuiten.
  const m = s.match(/-?\d[\d.,]*(?:\/\d+)?/);
  if (!m) return null;
  let tok = m[0];

  // Eenvoudige breuk a/b → a gedeeld door b (0,5 voor "1/2").
  const breuk = tok.match(/^(-?\d+)\/(\d+)$/);
  if (breuk) {
    const noemer = Number(breuk[2]);
    if (noemer === 0) return null;
    return Number(breuk[1]) / noemer;
  }

  // Komma én punt aanwezig: de laatste is de decimaalscheider, de andere is een
  // duizendtalscheider die eruit mag. Bij één scheider behandelen we die als
  // decimaal — een vmbo-leerling typt "2,5" of "2.5", geen duizendtallen.
  if (tok.includes(',') && tok.includes('.')) {
    if (tok.lastIndexOf(',') > tok.lastIndexOf('.')) tok = tok.replace(/\./g, '').replace(',', '.');
    else tok = tok.replace(/,/g, '');
  } else if (tok.includes(',')) {
    tok = tok.replace(',', '.');
  }

  const n = Number(tok);
  return Number.isFinite(n) ? n : null;
}

/** Bevat de rauwe invoer de gevraagde eenheid? (spatie- en hoofdletterongevoelig) */
function bevatEenheid(invoer, eenheid) {
  if (!eenheid) return true;
  const schoon = (t) => String(t ?? '').toLowerCase().replace(/\s+/g, '')
    .replace(/2/g, '²').replace(/3/g, '³'); // m2 ~ m², m3 ~ m³
  return schoon(invoer).includes(schoon(eenheid));
}

/**
 * Score een numerieke vraag met tolerantie.
 *
 * @param {string|number} antwoord
 * @param {object} vraag  { verwacht_getal, tolerantie_abs?, tolerantie_rel?,
 *                          eenheid?, eenheid_verplicht?, maximumscore }
 * @returns {{score, max, goed, gegeven, reden}}
 */
export function scoreGetal(antwoord, vraag) {
  const max = vraag.maximumscore ?? 1;
  const gegeven = parseGetal(antwoord);
  const verwacht = Number(vraag.verwacht_getal);

  if (gegeven === null) {
    return { score: 0, max, goed: false, gegeven: null, reden: 'geen getal ingevuld' };
  }
  if (!Number.isFinite(verwacht)) {
    return { score: 0, max, goed: false, gegeven, reden: 'geen verwacht getal ingesteld' };
  }

  const absMarge = Number(vraag.tolerantie_abs) || 0;
  const relMarge = (Number(vraag.tolerantie_rel) || 0) * Math.abs(verwacht);
  const marge = Math.max(absMarge, relMarge);
  const verschil = Math.abs(gegeven - verwacht);
  const getalGoed = verschil <= marge + 1e-9;

  // Eenheid alleen afdwingen als de vraag dat expliciet vraagt.
  if (getalGoed && vraag.eenheid_verplicht && !bevatEenheid(antwoord, vraag.eenheid)) {
    return { score: 0, max, goed: false, gegeven,
      reden: `getal klopt, maar de eenheid (${vraag.eenheid}) ontbreekt` };
  }

  return {
    score: getalGoed ? max : 0, max, goed: getalGoed, gegeven,
    reden: getalGoed ? (verschil === 0 ? 'exact goed' : 'binnen de marge')
                     : `${gegeven} wijkt af van ${verwacht}`,
  };
}

/** Score een kort-antwoord-vraag. Hoofdletter-, spatie- en accent-ongevoelig. */
export function scoreKortAntwoord(antwoord, vraag) {
  const max = vraag.maximumscore ?? 1;
  const norm = (t) => String(t ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // accenten weg: café → cafe
    .toLowerCase().replace(/\s+/g, ' ').trim();
  const gegeven = norm(antwoord);
  if (!gegeven) return { score: 0, max, goed: false, reden: 'leeg' };

  const varianten = (vraag.geaccepteerde_antwoorden || []).map(norm);
  const goed = varianten.includes(gegeven);
  return { score: goed ? max : 0, max, goed, reden: goed ? 'komt overeen' : 'niet in de lijst' };
}

/** Score een meerkeuzevraag. Leeg antwoord of ontbrekende sleutel = 0, nooit gratis punten. */
export function scoreMeerkeuze(gekozenLetter, vraag) {
  const max = vraag.maximumscore ?? 1;
  const gekozen = String(gekozenLetter ?? '').trim().toUpperCase();
  const juist = String(vraag.correcte_optie ?? '').trim().toUpperCase();
  if (!gekozen || !juist) return { score: 0, max, goed: false, reden: 'geen keuze of geen sleutel' };
  const goed = gekozen === juist;
  return { score: goed ? max : 0, max, goed, reden: goed ? 'juiste optie' : 'onjuiste optie' };
}

/**
 * Tel de zelfscore van een berekening-met-stappen op.
 * @param {Array<{stap:number, punten:number}>} model
 * @param {Object<number|string, boolean>} toegekend  per stap: verdiend?
 */
export function scoreZelfPerPunt(model, toegekend) {
  const rijen = Array.isArray(model) ? model : [];
  const max = rijen.reduce((s, r) => s + Math.max(0, Number(r.punten) || 0), 0);
  const score = rijen.reduce(
    (s, r) => s + (toegekend?.[r.stap] ? Math.max(0, Number(r.punten) || 0) : 0), 0);
  return { score: Math.min(Math.max(0, score), max), max };
}

/** Eén ingang die op vraagtype dispatcht. Open/teken-vragen geven null (die horen bij AI/zelfscore). */
export function scoreVraag(antwoord, vraag) {
  switch (vraag.vraagtype) {
    case 'getal':
    case 'getal_met_eenheid': return scoreGetal(antwoord, vraag);
    case 'kort_antwoord':     return scoreKortAntwoord(antwoord, vraag);
    case 'meerkeuze':         return scoreMeerkeuze(antwoord, vraag);
    default:                  return null;
  }
}
